import React, { useState, useEffect } from 'react';
import { parseApproximateComponents, MONTH_NAMES } from '@/utils/taskLogic';
import { TIMING_YEAR_OPTIONS } from '@/utils/dateFormat';

// Structured "season" input helper: Spring/Summer/Autumn/Winter + primo/medio/ultimo, resolved
// to one of the EXISTING canonical "primo|medio|ultimo <Month> <Year>" strings already
// understood by `parseApproximateComponents`/`getApproximateLabel`/`calculateInterval` in
// `src/utils/taskLogic.js`. This component never produces a new string shape - it's a friendlier
// way to pick a month that `taskLogic.js` already knows how to parse, so no parsing/due-date
// logic changes anywhere.
//
// Meteorological DK seasons. Each entry is { month (0-indexed), yearOffset }, indexed by
// POSITIONS (primo=0, medio=1, ultimo=2). Winter is the only season crossing a year boundary:
// its "year" is the year Winter *starts* in, so primo (December) has offset 0, while medio
// (January) and ultimo (February) fall in the following calendar year, offset +1. E.g. "Winter
// 2026" -> primo = December 2026, medio = January 2027, ultimo = February 2027.
const SEASONS = {
  Spring: [{ month: 2, yearOffset: 0 }, { month: 3, yearOffset: 0 }, { month: 4, yearOffset: 0 }],
  Summer: [{ month: 5, yearOffset: 0 }, { month: 6, yearOffset: 0 }, { month: 7, yearOffset: 0 }],
  Autumn: [{ month: 8, yearOffset: 0 }, { month: 9, yearOffset: 0 }, { month: 10, yearOffset: 0 }],
  Winter: [{ month: 11, yearOffset: 0 }, { month: 0, yearOffset: 1 }, { month: 1, yearOffset: 1 }],
};
const SEASON_NAMES = Object.keys(SEASONS);
const POSITIONS = ['primo', 'medio', 'ultimo'];

// Default fallback when this mode is entered with no existing value yet - the year *options*
// list itself is the shared TIMING_YEAR_OPTIONS (imported above), not derived from this. Note
// this is a SEASON-LABEL year, distinct from the resolved calendar year in `resolve()` below -
// for Winter's medio/ultimo that calendar year can land one past TIMING_YEAR_END (e.g. label
// year = TIMING_YEAR_END -> ultimo Winter resolves to February TIMING_YEAR_END+1). That's the
// existing, intentional Winter year-crossing behavior, unaffected by widening this range.
const currentYear = new Date().getFullYear();

const selectClass = 'rounded border px-2 py-1.5 bg-white dark:bg-gray-800 text-sm';

// Given a resolved calendar month (0-indexed) and position, find which season+yearOffset it
// belongs to. Every month 0-11 appears in exactly one season's array, so this is unambiguous.
const findSeasonFor = (monthIndex) => {
  for (const season of SEASON_NAMES) {
    const idx = SEASONS[season].findIndex((e) => e.month === monthIndex);
    if (idx !== -1) return { season, position: POSITIONS[idx], yearOffset: SEASONS[season][idx].yearOffset };
  }
  return null;
};

// Reverse-map an existing "position Month Year" value back to { season, position, year }
// (year = the season-label year, e.g. "medio January 2027" -> Winter 2026). Returns null if
// `value` isn't a month-position shaped approximate-timing string at all.
const reverseMap = (value) => {
  const parsed = parseApproximateComponents(value);
  if (parsed?.mode !== 'month') return null;
  const found = findSeasonFor(parsed.monthIndex);
  if (!found) return null;
  return {
    season: found.season,
    position: found.position,
    year: String(Number(parsed.year) - found.yearOffset),
  };
};

// Forward-resolve { season, position, year } to the canonical string + the human-readable
// month/year it resolves to (used both to emit and to render the preview line).
const resolve = (season, position, year) => {
  const idx = POSITIONS.indexOf(position);
  const entry = SEASONS[season]?.[idx];
  if (!entry || !year) return null;
  const targetYear = String(Number(year) + entry.yearOffset);
  return { monthName: MONTH_NAMES[entry.month], targetYear, canonical: `${position} ${MONTH_NAMES[entry.month]} ${targetYear}` };
};

export default function SeasonsSelect({ value, onChange }) {
  const initial = reverseMap(value);

  const [season, setSeason] = useState(initial?.season || 'Spring');
  const [position, setPosition] = useState(initial?.position || 'primo');
  const [year, setYear] = useState(initial?.year || String(currentYear));

  const emit = (nextSeason, nextPosition, nextYear) => {
    const result = resolve(nextSeason, nextPosition, nextYear);
    if (result) onChange(result.canonical);
  };

  // Re-sync local state if the incoming value changes from outside (e.g. switching between
  // diary entries being edited).
  useEffect(() => {
    const mapped = reverseMap(value);
    if (mapped) {
      setSeason(mapped.season);
      setPosition(mapped.position);
      setYear(mapped.year);
    }
  }, [value]);

  // First time this mode is entered (no existing month-position value yet) - emit a sensible
  // default immediately rather than leaving the field blank.
  useEffect(() => {
    if (!reverseMap(value)) {
      emit(season, position, year);
    }
    // Intentionally run once on mount only - subsequent syncs are handled by the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = resolve(season, position, year);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <select
          value={season}
          onChange={(e) => {
            setSeason(e.target.value);
            emit(e.target.value, position, year);
          }}
          className={selectClass}
        >
          {SEASON_NAMES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={position}
          onChange={(e) => {
            setPosition(e.target.value);
            emit(season, e.target.value, year);
          }}
          className={selectClass}
        >
          {POSITIONS.map((p) => (
            <option key={p} value={p}>
              {p[0].toUpperCase() + p.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => {
            setYear(e.target.value);
            emit(season, position, e.target.value);
          }}
          className={selectClass}
        >
          {TIMING_YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      {preview && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          → gemmes som: {preview.canonical}
        </p>
      )}
    </div>
  );
}
